import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Paper,
  Divider,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import { useSiteStore } from '../../store/site.store';
import { usePageReadiness, useSinglePageReadiness } from '../../hooks/usePageReadiness';
import { ActivationEventType, ReadinessLevel, IntentType, TopQuery, PageReadinessRecommendationDetail } from '../../types/mode.types';
import { activationService } from '../../services/activationService';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseJsonValue = (value: unknown): unknown => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed || !['[', '{'].includes(trimmed[0])) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const unwrapEvidenceValue = (value: unknown): unknown => {
  const parsed = parseJsonValue(value);
  if (isRecord(parsed) && 'value' in parsed) {
    return parseJsonValue(parsed.value);
  }
  return parsed;
};

const textListValue = (value: unknown): string[] => {
  const parsed = unwrapEvidenceValue(value);
  if (Array.isArray(parsed)) {
    return parsed.map((item) => String(item || '').trim()).filter(Boolean);
  }
  if (typeof parsed === 'string' && parsed.trim()) {
    return [parsed.trim()];
  }
  return [];
};

const h1TextEvidence = (evidence?: Record<string, unknown>) => {
  if (!evidence) return [];
  return textListValue(evidence.h1Texts ?? evidence.h1_texts);
};

const PageReadinessDetail: React.FC = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const { selectedSite } = useSiteStore();
  const siteId = selectedSite?.id || 0;

  console.log('🔍 PageReadinessDetail Debug:', { 
    pageId, 
    siteId, 
    selectedSite,
    parsedPageId: parseInt(pageId || '0')
  });

  const { data: pageData, isLoading, error } = useSinglePageReadiness(siteId, parseInt(pageId || '0'));
  const numericPageId = parseInt(pageId || '0', 10);
  const [selectedCheckScope, setSelectedCheckScope] = useState('all');
  const [selectedRecommendation, setSelectedRecommendation] = useState<PageReadinessRecommendationDetail | null>(null);
  const { data: pageList = [] } = usePageReadiness(siteId, 0, 100);

  console.log('🔍 Query State:', { pageData, isLoading, error });

  useEffect(() => {
    if (selectedSite?.id && pageData) {
      const sessionId = sessionStorage.getItem('sessionId') || `session_${Date.now()}`;
      activationService.trackEvent(selectedSite.id, {
        eventType: ActivationEventType.PAGE_READINESS_VIEWED,
        sessionId,
        metadata: {
          pageId: parseInt(pageId || '0'),
          pageUrl: pageData.url,
          readinessLevel: pageData.overallScore.level,
          readinessScore: pageData.overallScore.score
        }
      }).catch(err => console.error('Failed to track page readiness view:', err));
    }
  }, [selectedSite?.id, pageData]);

  const getReadinessColor = (level: ReadinessLevel) => {
    switch (level) {
      case 'EXCELLENT':
        return 'success';
      case 'GOOD':
        return 'info';
      case 'NEEDS_WORK':
        return 'warning';
      case 'POOR':
        return 'error';
      default:
        return 'default';
    }
  };

  const getIntentLabel = (intent: IntentType) => {
    const labels: Record<IntentType, string> = {
      INFORMATIONAL: '📚 Informational',
      COMMERCIAL: '🛍️ Commercial',
      TRANSACTIONAL: '💳 Transactional',
      NAVIGATIONAL: '🧭 Navigational',
      MIXED: '🔀 Mixed',
      UNKNOWN: '❓ Unknown',
    };
    return labels[intent] || '❓ Unknown';
  };

  const getIntentChipColor = (intent: IntentType): 'info' | 'warning' | 'success' | 'secondary' | 'default' => {
    switch (intent) {
      case 'INFORMATIONAL':  return 'info';
      case 'COMMERCIAL':     return 'warning';
      case 'TRANSACTIONAL':  return 'success';
      case 'NAVIGATIONAL':   return 'secondary';
      default:               return 'default';
    }
  };

  const getStatusIcon = (status: 'PASS' | 'FAIL' | 'WARNING' | 'INFO') => {
    switch (status) {
      case 'PASS':
        return <CheckCircleIcon color="success" />;
      case 'FAIL':
        return <ErrorIcon color="error" />;
      case 'WARNING':
        return <WarningIcon color="warning" />;
      case 'INFO':
        return <InfoIcon color="info" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: 'PASS' | 'FAIL' | 'WARNING' | 'INFO'): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'PASS':
        return 'success';
      case 'FAIL':
        return 'error';
      case 'WARNING':
        return 'warning';
      case 'INFO':
        return 'info';
      default:
        return 'default';
    }
  };

  if (!siteId || !pageId) {
    return (
      <Alert severity="warning">
        Missing site or page information. Please select a site and try again.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !pageData) {
    return (
      <Alert severity="error">
        Failed to load page readiness details. Please try again.
        {error && <Box sx={{ mt: 1, fontSize: '0.875rem' }}>Error: {error.message}</Box>}
      </Alert>
    );
  }

  const ctr = pageData.impressions > 0 ? (pageData.clicks / pageData.impressions) * 100 : 0;
  const currentPageIndex = pageList.findIndex((page) => page.pageId === numericPageId);
  const previousPage = currentPageIndex > 0 ? pageList[currentPageIndex - 1] : null;
  const nextPage = currentPageIndex >= 0 && currentPageIndex < pageList.length - 1 ? pageList[currentPageIndex + 1] : null;

  const formatScopeScore = (score?: number, maxScore?: number) => {
    if (score == null || maxScore == null) return '—';
    return `${Math.round(score)}/${Math.round(maxScore)}`;
  };

  const formatMetricLabel = (key: string) => key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const formatMetricValue = (value: unknown) => {
    if (value == null) return '—';
    const parsed = unwrapEvidenceValue(value);
    if (Array.isArray(parsed)) return parsed.join(', ');
    if (isRecord(parsed)) return JSON.stringify(parsed);
    if (typeof parsed === 'boolean') return parsed ? 'Yes' : 'No';
    if (typeof parsed === 'number') return Number.isInteger(parsed) ? parsed.toLocaleString() : parsed.toFixed(1);
    return String(parsed);
  };

  const formatCheckEvidence = (evidence?: Record<string, unknown>) => {
    if (!evidence || Object.keys(evidence).length === 0) return [];
    return Object.entries(evidence)
      .filter(([key]) => key !== 'h1Texts' && key !== 'h1_texts')
      .filter(([, value]) => value != null && value !== '')
      .slice(0, 4);
  };

  const formatRecommendationEvidence = (evidence?: Record<string, unknown>) => {
    if (!evidence) return [];
    return Object.entries(evidence)
      .filter(([key]) => key !== 'h1Texts' && key !== 'h1_texts')
      .filter(([, value]) => {
        const unwrapped = unwrapEvidenceValue(value);
        return unwrapped != null && unwrapped !== '' && (!Array.isArray(unwrapped) || unwrapped.length > 0);
      });
  };

  const checksByScope = pageData.checks.reduce<Record<string, typeof pageData.checks>>((groups, check) => {
    const scopeId = check.scopeId || 'other';
    groups[scopeId] = groups[scopeId] || [];
    groups[scopeId].push(check);
    return groups;
  }, {});
  const checkScopeEntries = Object.entries(checksByScope).sort(([left], [right]) => {
    if (left === 'other') return 1;
    if (right === 'other') return -1;
    return left.localeCompare(right);
  });
  const checkScopeLabel = (scopeId: string) => {
    const scope = pageData.scopes?.find((item) => item.scopeId === scopeId);
    return scope?.scopeName || formatMetricLabel(scopeId);
  };
  const visibleChecks = selectedCheckScope === 'all'
    ? pageData.checks
    : checksByScope[selectedCheckScope] || [];
  const recommendationDetails = pageData.recommendationDetails;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box mb={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/early-stage/pages')}
          >
            Back to Pages
          </Button>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              disabled={!previousPage}
              onClick={() => previousPage && navigate(`/early-stage/pages/${previousPage.pageId}`)}
            >
              Previous Page
            </Button>
            <Button
              variant="outlined"
              endIcon={<ArrowForwardIcon />}
              disabled={!nextPage}
              onClick={() => nextPage && navigate(`/early-stage/pages/${nextPage.pageId}`)}
            >
              Next Page
            </Button>
          </Box>
        </Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Page Readiness Details
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
          {pageData.url}
        </Typography>
      </Box>

      {/* Score Overview */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">Overall Readiness</Typography>
                <Chip
                  label={pageData.overallScore.level}
                  color={getReadinessColor(pageData.overallScore.level) as any}
                />
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="h2" color="primary">
                  {pageData.overallScore.score}
                </Typography>
                <Box flexGrow={1}>
                  <LinearProgress
                    variant="determinate"
                    value={pageData.overallScore.score}
                    color={getReadinessColor(pageData.overallScore.level) as any}
                    sx={{ height: 10, borderRadius: 5, mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {pageData.overallScore.summary}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Page Metadata
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Title
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {pageData.title || 'No title'}
                  </Typography>
                </Grid>
                {pageData.description && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.5 }}>
                      {pageData.description}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Page Type
                  </Typography>
                  <Chip
                    label={pageData.pageType || 'PAGE'}
                    size="small"
                    variant="outlined"
                    color="default"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Query Intent
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={getIntentLabel(pageData.detectedIntent)}
                      size="small"
                      color={getIntentChipColor(pageData.detectedIntent)}
                    />
                    {pageData.intentConfidence != null && (
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(pageData.intentConfidence * 100)}%
                      </Typography>
                    )}
                  </Box>
                </Grid>
                {/* Enhanced indexing details */}
                {pageData.indexingDetails && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Indexing State
                      </Typography>
                      <Chip 
                        label={pageData.indexingDetails.coverageState}
                        size="small"
                        color={pageData.indexingDetails.coverageState === 'INDEXED' ? 'success' : 'error'}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Mobile Usability
                      </Typography>
                      <Chip 
                        label={pageData.indexingDetails.mobileUsabilityState || 'Unknown'}
                        size="small"
                        color={pageData.indexingDetails.mobileUsabilityState === 'MOBILE_FRIENDLY' ? 'success' : 'warning'}
                      />
                    </Grid>
                    {pageData.indexingDetails.lastCrawlTime && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Last Crawled: {new Date(pageData.indexingDetails.lastCrawlTime).toLocaleDateString()}
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}
                {/* Enhanced indexing details */}
                {pageData.indexingDetails && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Indexing State
                      </Typography>
                      <Chip 
                        label={pageData.indexingDetails.coverageState}
                        size="small"
                        color={pageData.indexingDetails.coverageState === 'INDEXED' ? 'success' : 'error'}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Mobile Usability
                      </Typography>
                      <Chip 
                        label={pageData.indexingDetails.mobileUsabilityState || 'Unknown'}
                        size="small"
                        color={pageData.indexingDetails.mobileUsabilityState === 'MOBILE_FRIENDLY' ? 'success' : 'warning'}
                      />
                    </Grid>
                    {pageData.indexingDetails.lastCrawlTime && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Last Crawled: {new Date(pageData.indexingDetails.lastCrawlTime).toLocaleDateString()}
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Data Sources
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {pageData.searchEngines && pageData.searchEngines.length > 0 ? (
                      pageData.searchEngines.map((engine) => (
                        <Chip
                          key={engine}
                          label={engine === 'GOOGLE' ? 'Google Search Console' : engine === 'BING' ? 'Bing Webmaster Tools' : engine}
                          size="small"
                          color={engine === 'GOOGLE' ? 'primary' : 'secondary'}
                          variant="outlined"
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        No search engine data available
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    Clicks
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {pageData.clicks.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    Impressions
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {pageData.impressions.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    CTR
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {ctr.toFixed(2)}%
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Page Readiness Scopes */}
      {pageData.scopes && pageData.scopes.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Page Readiness Scopes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Separate per-page scope signals used to connect recommendations with Action Center work.
            </Typography>
            <Grid container spacing={2}>
              {pageData.scopes.map((scope) => (
                <Grid item xs={12} md={6} lg={scope.scopeId === 'content_intent' ? 8 : 4} key={scope.scopeId}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} mb={1}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {scope.scopeId === 'technical_hygiene' ? 'On-Page Technical SEO' : scope.scopeName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {scope.summary}
                        </Typography>
                      </Box>
                      <Chip
                        label={scope.availabilityStatus === 'not_available'
                          ? 'Unavailable'
                          : formatScopeScore(scope.score, scope.maxScore)}
                        color={scope.availabilityStatus === 'not_available'
                          ? 'default'
                          : getReadinessColor(scope.level) as any}
                        size="small"
                      />
                    </Box>
                    {scope.availabilityStatus !== 'not_available' && (
                      <LinearProgress
                        variant="determinate"
                        value={scope.maxScore > 0 ? Math.min((scope.score / scope.maxScore) * 100, 100) : 0}
                        color={getReadinessColor(scope.level) as any}
                        sx={{ height: 8, borderRadius: 5 }}
                      />
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      {recommendationDetails.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Top Recommendations
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {recommendationDetails.map((detail, index) => (
                <Grid item xs={12} key={`${detail.checkId || detail.title}-${index}`}>
                  <Paper
                    variant="outlined"
                    component="button"
                    type="button"
                    onClick={() => setSelectedRecommendation(detail)}
                    sx={{
                      p: 2,
                      width: '100%',
                      textAlign: 'left',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      cursor: 'pointer',
                      '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                      '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.light', outlineOffset: 2 },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Chip label={index + 1} color="primary" size="small" />
                      <Box flex={1} minWidth={0}>
                        <Typography variant="body1">{detail.recommendation}</Typography>
                      </Box>
                      <ArrowForwardIcon color="action" />
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={selectedRecommendation !== null}
        onClose={() => setSelectedRecommendation(null)}
        fullWidth
        maxWidth="md"
      >
        {selectedRecommendation && (
          <>
            <DialogTitle sx={{ pr: 6 }}>
              {selectedRecommendation.title}
              <IconButton
                aria-label="Close recommendation details"
                onClick={() => setSelectedRecommendation(null)}
                sx={{ position: 'absolute', right: 12, top: 12 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 3 }}>
                <Chip label={selectedRecommendation.status} color={getStatusColor(selectedRecommendation.status)} />
                {selectedRecommendation.severity && <Chip label={`${formatMetricLabel(selectedRecommendation.severity)} severity`} variant="outlined" />}
                {selectedRecommendation.scopeId && <Chip label={checkScopeLabel(selectedRecommendation.scopeId)} variant="outlined" />}
                {typeof selectedRecommendation.score === 'number' && <Chip label={`Score ${Math.round(selectedRecommendation.score)}`} variant="outlined" />}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Issue</Typography>
                <Typography variant="body1">{selectedRecommendation.issue || 'No issue details were provided.'}</Typography>
              </Box>

              <Alert severity={selectedRecommendation.status === 'FAIL' ? 'error' : 'warning'} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={700}>Recommendation</Typography>
                <Typography variant="body2">{selectedRecommendation.recommendation}</Typography>
              </Alert>

              {(selectedRecommendation.checkId || selectedRecommendation.actionType || selectedRecommendation.reasonCode) && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>Check details</Typography>
                  <Grid container spacing={1.5}>
                    {selectedRecommendation.checkId && (
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">Check ID</Typography>
                        <Typography variant="body2" fontWeight={600}>{selectedRecommendation.checkId}</Typography>
                      </Grid>
                    )}
                    {selectedRecommendation.actionType && (
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">Action type</Typography>
                        <Typography variant="body2" fontWeight={600}>{selectedRecommendation.actionType}</Typography>
                      </Grid>
                    )}
                    {selectedRecommendation.reasonCode && (
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">Reason code</Typography>
                        <Typography variant="body2" fontWeight={600}>{selectedRecommendation.reasonCode}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {(selectedRecommendation.affectedUrl || selectedRecommendation.targetUrl) && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {selectedRecommendation.affectedUrl && (
                    <Grid item xs={12} md={selectedRecommendation.targetUrl ? 6 : 12}>
                      <Typography variant="caption" color="text.secondary">Affected page</Typography>
                      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{selectedRecommendation.affectedUrl}</Typography>
                    </Grid>
                  )}
                  {selectedRecommendation.targetUrl && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Target page</Typography>
                      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{selectedRecommendation.targetUrl}</Typography>
                    </Grid>
                  )}
                </Grid>
              )}

              {h1TextEvidence(selectedRecommendation.evidence).length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>Headings found on the page</Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {h1TextEvidence(selectedRecommendation.evidence).map((text, index) => (
                      <Chip key={`${text}-${index}`} label={text} variant="outlined" sx={{ maxWidth: '100%', '& .MuiChip-label': { whiteSpace: 'normal' } }} />
                    ))}
                  </Box>
                </Box>
              )}

              {formatRecommendationEvidence(selectedRecommendation.evidence).length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Evidence</Typography>
                  <Grid container spacing={1.5}>
                    {formatRecommendationEvidence(selectedRecommendation.evidence).map(([key, value]) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                          <Typography variant="caption" color="text.secondary">{formatMetricLabel(key)}</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>{formatMetricValue(value)}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedRecommendation(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Query Intent Details */}
      {pageData.topQueries && pageData.topQueries.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="h6">Query Intent Details</Typography>
              <Chip
                label={`${pageData.topQueries.length} queries`}
                size="small"
                variant="outlined"
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Search queries that brought users to this page (from Google Search Console), classified by intent.
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Query</strong></TableCell>
                    <TableCell align="center"><strong>Intent</strong></TableCell>
                    <TableCell align="right"><strong>Impressions</strong></TableCell>
                    <TableCell align="right"><strong>Clicks</strong></TableCell>
                    <TableCell align="right"><strong>CTR</strong></TableCell>
                    <TableCell align="right"><strong>Avg Position</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageData.topQueries.map((q: TopQuery, index: number) => {
                    const qCtr = q.impressions > 0 ? (q.clicks / q.impressions) * 100 : 0;
                    return (
                      <TableRow key={index} hover>
                        <TableCell sx={{ maxWidth: 320, wordBreak: 'break-word' }}>
                          {q.query}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={getIntentLabel(q.intent)}
                            size="small"
                            color={getIntentChipColor(q.intent)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">{q.impressions.toLocaleString()}</TableCell>
                        <TableCell align="right">{q.clicks.toLocaleString()}</TableCell>
                        <TableCell align="right">{qCtr.toFixed(2)}%</TableCell>
                        <TableCell align="right">
                          {q.avgPosition !== null ? Number(q.avgPosition).toFixed(1) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Keyword Performance Stats */}
      {pageData.keywordStats && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Keyword Performance
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={2}>
                <Typography variant="body2" color="text.secondary">
                  Total Keywords
                </Typography>
                <Typography variant="h5" color="primary">
                  {pageData.keywordStats.totalKeywords}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="body2" color="text.secondary">
                  Top 3 Positions
                </Typography>
                <Typography variant="h5" color="success.main">
                  {pageData.keywordStats.top3Keywords}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="body2" color="text.secondary">
                  Top 10 Positions
                </Typography>
                <Typography variant="h5" color="info.main">
                  {pageData.keywordStats.top10Keywords}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="body2" color="text.secondary">
                  Avg Position
                </Typography>
                <Typography variant="h5" color="warning.main">
                  {pageData.keywordStats.avgPosition.toFixed(1)}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="body2" color="text.secondary">
                  Total Clicks
                </Typography>
                <Typography variant="h5" color="primary">
                  {pageData.keywordStats.totalClicks.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="body2" color="text.secondary">
                  Total Impressions
                </Typography>
                <Typography variant="h5" color="text.primary">
                  {pageData.keywordStats.totalImpressions.toLocaleString()}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Multi-Engine Performance */}
      {pageData.engineMetrics && pageData.engineMetrics.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Search Engine Performance
            </Typography>
            <Grid container spacing={2}>
              {pageData.engineMetrics.map((engine) => (
                <Grid item xs={12} md={6} key={engine.name}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <Box 
                        sx={{ 
                          width: 12, 
                          height: 12, 
                          borderRadius: '50%', 
                          bgcolor: engine.name === 'GOOGLE' ? '#4285f4' : '#00809d'
                        }}
                      />
                      <Typography variant="subtitle1" fontWeight="bold">
                        {engine.name === 'GOOGLE' ? 'Google Search Console' : 'Bing Webmaster Tools'}
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Clicks</Typography>
                        <Typography variant="h6">{engine.clicks.toLocaleString()}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Avg Position</Typography>
                        <Typography variant="h6">{engine.avgPosition.toFixed(1)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Impressions</Typography>
                        <Typography variant="h6">{engine.impressions.toLocaleString()}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">CTR</Typography>
                        <Typography variant="h6">{(engine.ctr * 100).toFixed(2)}%</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Keywords: {engine.keywordCount}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Local Business Performance */}
      {pageData.localBusinessMetrics && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Local Business Performance
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={2}>
                <Typography variant="body2" color="text.secondary">Profile Views</Typography>
                <Typography variant="h5" color="primary">
                  {pageData.localBusinessMetrics.profileViews.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="body2" color="text.secondary">Search Views</Typography>
                <Typography variant="h5" color="primary">
                  {pageData.localBusinessMetrics.searchViews.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="body2" color="text.secondary">Maps Views</Typography>
                <Typography variant="h5" color="primary">
                  {pageData.localBusinessMetrics.mapsViews.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="body2" color="text.secondary">Phone Calls</Typography>
                <Typography variant="h5" color="success.main">
                  {pageData.localBusinessMetrics.calls.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="body2" color="text.secondary">Directions</Typography>
                <Typography variant="h5" color="success.main">
                  {pageData.localBusinessMetrics.directions.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="body2" color="text.secondary">Website Clicks</Typography>
                <Typography variant="h5" color="success.main">
                  {pageData.localBusinessMetrics.websiteClicks.toLocaleString()}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Keyword Opportunities */}
      {pageData.keywordOpportunities && pageData.keywordOpportunities.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Keyword Opportunities (Positions 11-30)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These keywords are close to page 1 and represent the best opportunities for improvement.
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>Keyword</th>
                    <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>Position</th>
                    <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>Clicks</th>
                    <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>Impressions</th>
                    <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>CTR</th>
                    <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>Device</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.keywordOpportunities.slice(0, 10).map((opportunity, index) => (
                    <tr key={index}>
                      <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>
                        {opportunity.query}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>
                        <Chip 
                          label={opportunity.position.toFixed(1)} 
                          size="small"
                          color={opportunity.position <= 15 ? 'warning' : 'default'}
                        />
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>
                        {opportunity.clicks}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>
                        {opportunity.impressions.toLocaleString()}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>
                        {(opportunity.ctr * 100).toFixed(2)}%
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>
                        <Chip label={opportunity.device || 'ALL'} size="small" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Readiness Checks */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Readiness Checks
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Checks are grouped by the same readiness scope categories used above.
          </Typography>
          <Tabs
            value={selectedCheckScope}
            onChange={(_, value) => setSelectedCheckScope(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label={`All (${pageData.checks.length})`} value="all" />
            {checkScopeEntries.map(([scopeId, checks]) => (
              <Tab key={scopeId} label={`${checkScopeLabel(scopeId)} (${checks.length})`} value={scopeId} />
            ))}
          </Tabs>
          <List>
            {visibleChecks.map((check, index) => (
              <React.Fragment key={`${check.scopeId || 'other'}-${check.checkId || check.checkName}-${index}`}>
                <ListItem alignItems="flex-start">
                  <ListItemIcon sx={{ mt: 1 }}>
                    {getStatusIcon(check.status)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="subtitle1" fontWeight="bold">
                          {check.checkName}
                        </Typography>
                        <Chip
                          label={check.status}
                          size="small"
                          color={getStatusColor(check.status) as any}
                        />
                        {check.scopeId && <Chip label={checkScopeLabel(check.scopeId)} size="small" variant="outlined" />}
                        {check.checkId && <Chip label={check.checkId} size="small" variant="outlined" />}
                        {check.actionType && <Chip label={check.actionType} size="small" color="primary" variant="outlined" />}
                        {typeof check.score === 'number' && <Chip label={`Score ${Math.round(check.score)}`} size="small" variant="outlined" />}
                      </Box>
                    }
                    secondary={
                      <Box component="div">
                        {h1TextEvidence(check.evidence).length > 0 && (
                          <Box sx={{ mt: 1, mb: 1 }}>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Crawler-observed H1 texts
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={1}>
                              {h1TextEvidence(check.evidence).map((text, h1Index) => (
                                <Chip
                                  key={`${text}-${h1Index}`}
                                  label={text}
                                  size="small"
                                  variant="outlined"
                                  color={h1Index === 0 ? 'primary' : 'warning'}
                                  sx={{ maxWidth: '100%', '& .MuiChip-label': { whiteSpace: 'normal' } }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                        {check.message && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {check.message}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                          <strong>Issue:</strong> {check.issue || 'No issue detected'}
                        </Typography>
                        {formatCheckEvidence(check.evidence).length > 0 && (
                          <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                            {formatCheckEvidence(check.evidence).map(([key, value]) => (
                              <Chip
                                key={key}
                                label={`${formatMetricLabel(key)}: ${formatMetricValue(value)}`}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        )}
                        {check.recommendation && (
                          <Alert severity={check.status === 'FAIL' ? 'error' : 'info'} sx={{ mt: 1 }}>
                            <strong>Recommendation:</strong> {check.recommendation}
                          </Alert>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < visibleChecks.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PageReadinessDetail;
